import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import {
    Speed as SpeedIcon,
    ReceiptLong as ReceiptLongIcon,
    CloudUpload as CloudUploadIcon,
    PieChart as PieChartIcon,
    CheckCircle as CheckIcon,
    Star as StarIcon,
} from '@mui/icons-material';

function BenefitItem({ text }) {
    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <CheckIcon fontSize="small" sx={{ color: 'orange' }} />
            <Typography variant="body2">{text}</Typography>
        </Stack>
    );
}

function KpiCard({ label, value, highlight }) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2,
                minWidth: 180,
                flex: 1,
                borderColor: highlight ? 'warning.light' : 'divider',
                background: highlight ? 'rgba(255,165,0,0.08)' : 'transparent',
            }}
        >
            <CardContent>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
}

export default function LandingHero({ onStart }) {
    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                py: { xs: 8, md: 12 },
                background:
                    'radial-gradient(1200px 600px at 10% -10%, rgba(255,165,0,0.15), transparent), radial-gradient(800px 500px at 100% 0%, rgba(255,165,0,0.12), transparent)',
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Chip
                        icon={<SpeedIcon />}
                        label="Buchhaltung in Minuten – nicht in Stunden"
                        sx={{ mb: 2, borderRadius: 1.5 }}
                        variant="outlined"
                        color="warning"
                    />
                    <Typography variant="h3" component="h1" fontWeight={800} lineHeight={1.1}>
                        Buchhaltung, die <Box component="span" sx={{ color: 'orange' }}>einfach</Box> läuft.
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                        Schreiberlein automatisiert deine Buchhaltung: Rechnungen, Belege und Steuern an einem Ort – klar,
                        schnell, zuverlässig. Perfekt für Solo-Selbständige &amp; kleine Unternehmen.
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mt: 3 }}>
                        <Button
                            size="large"
                            variant="contained"
                            onClick={onStart}
                            sx={{
                                bgcolor: 'orange',
                                '&:hover': { bgcolor: '#cc7a00' },
                                borderRadius: 2,
                                px: 4,
                                fontWeight: 700,
                                boxShadow: '0 10px 22px rgba(255,140,0,0.35)',
                            }}
                        >
                            Jetzt starten
                        </Button>
                    </Stack>

                    <Stack direction="row" gap={2} sx={{ mt: 3 }} flexWrap="wrap">
                        <BenefitItem text="Digitale Rechnungsverwaltung" />
                        <BenefitItem text="OCR &amp; Beleg-Parsing" />
                        <BenefitItem text="Umsatzsteuer &amp; ESt" />
                    </Stack>

                    <Stack direction="row" alignItems="center" gap={1.5} sx={{ mt: 3 }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <StarIcon key={i} fontSize="small" sx={{ color: 'orange' }} />
                        ))}
                        <Typography variant="body2" color="text.secondary">
                            „Endlich weniger Papierkram“ – echte Nutzerstimme
                        </Typography>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            backdropFilter: 'blur(2px)',
                        }}
                    >
                        <CardContent>
                            <Typography variant="overline" color="text.secondary">
                                Vorschau
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                                USt-Voranmeldung – September
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack direction="row" gap={2} useFlexGap flexWrap="wrap">
                                <KpiCard label="Nettoumsatz" value="€ 12.480,00" />
                                <KpiCard label="USt gesamt" value="€ 2.371,20" />
                                <KpiCard label="Vorsteuer" value="€ 1.210,00" />
                                <KpiCard label="Zahllast" value="€ 1.161,20" highlight />
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                <Chip icon={<ReceiptLongIcon />} label="Rechnungen" variant="outlined" />
                                <Chip icon={<CloudUploadIcon />} label="Belege scannen" variant="outlined" />
                                <Chip icon={<PieChartIcon />} label="Auswertungen" variant="outlined" />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
